import React from 'react';
import { MdOutlineHomeWork } from 'react-icons/md';
import { SectionCard, Pill, Note, StatTile } from './ui';
import { signalTone } from './TreeCanopyCard';
import { fmtNum, fmtPct, fmtSqft, fmtDate, fmtDateTime, fmtRatioPct, safeArray, isNum, DASH, STATUS_LABELS } from '../format';

/**
 * Building footprints (property-context 2.2.0): Overture building outlines on
 * the parcel. A footprint says where a structure stands and how big it is —
 * never what it is. `class` / `subtype` are shown verbatim only when the
 * source carries them; nothing here infers a building type from its size.
 *
 * `summary.buildings_on_parcel` counts only `on_parcel: true` buildings.
 * Buildings with `on_parcel: false` are neighbours' structures that cross the
 * line and are always presented as such, never as the lot's buildings.
 */

export const FOOTPRINT_ATTRIBUTION = 'Building footprints: Overture Maps Foundation buildings';

const SIGNAL_LABELS = {
  VACANT_OF_STRUCTURES: 'No structures',
  STRUCTURE_CROSSES_BOUNDARY: 'Structure crosses boundary',
  FOOTPRINTS_NOT_COVERED: 'County not loaded',
  NO_PARCEL_POLYGON: 'No parcel polygon',
  FOOTPRINT_SOURCE_UNAVAILABLE: 'Source unavailable',
};

export function footprintSignalLabel(code) {
  if (!code) return 'Signal';
  return SIGNAL_LABELS[code] || String(code).toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

const metres = (v, digits = 1) => (isNum(v) ? `${fmtNum(v, digits)} m` : DASH);
const feet = (m) => (isNum(m) ? `${fmtNum(m * 3.28084)} ft` : null);

/** On-parcel buildings first (largest first), then the neighbours' structures that cross the line. */
export function buildingRows(buildings) {
  const rows = safeArray(buildings).filter((b) => b && typeof b === 'object');
  const rank = (b) => (b.on_parcel === true ? 0 : 1);
  return rows.slice().sort((a, b) => rank(a) - rank(b) || (Number(b.area_sqft) || 0) - (Number(a.area_sqft) || 0));
}

/** One sentence for the surround block: count in the ring, nearest structure, footprint area, tallest. */
export function footprintSurroundSentence(surround, radiusFt) {
  if (!surround) return null;
  const radius = isNum(radiusFt) ? `${fmtNum(radiusFt)} ft` : 'the search radius';
  const count = surround.buildings_within_radius;
  const parts = [];
  parts.push(`Within ${radius}: ${isNum(count) ? `${fmtNum(count)} building${count === 1 ? '' : 's'}` : DASH}`);
  if (isNum(surround.nearest_building_ft)) parts.push(`nearest ${fmtNum(surround.nearest_building_ft)} ft from the line`);
  else if (surround.nearest_building_ft === null && count === 0) parts.push(`no structures within ${radius}`);
  if (isNum(surround.footprint_sqft_within_radius)) parts.push(`${fmtSqft(surround.footprint_sqft_within_radius)} of footprint`);
  if (isNum(surround.max_height_m)) parts.push(`tallest ${fmtNum(surround.max_height_m, 1)} m${isNum(surround.heights_known_pct) ? ` (heights known for ${fmtPct(surround.heights_known_pct, 0)})` : ''}`);
  return parts.join(' · ');
}

/** County wording for the not-covered state: the resolved county name when we have one, else the FIPS. */
export function countyLabel(countyName, countyFips) {
  if (countyName) return countyName;
  if (countyFips) return `county FIPS ${countyFips}`;
  return 'this county';
}

function hasSignal(signals, code) {
  return safeArray(signals).some((s) => s && s.code === code);
}

export default function BuildingFootprintsCard({ data, status, countyName }) {
  const st = status?.status;
  const summary = data?.summary || null;
  const scope = data?.scope || {};
  const source = scope.source || {};
  const signals = safeArray(data?.signals);
  const notes = safeArray(data?.notes);
  const warnings = safeArray(status?.warnings);
  const county = countyLabel(countyName, scope.county_fips);
  const notCovered = st === 'not_covered' || hasSignal(signals, 'FOOTPRINTS_NOT_COVERED');
  const hasSummary = Boolean(summary) && isNum(summary.buildings_on_parcel);
  const pointBox = scope.footprint === 'point_box_60m' || hasSignal(signals, 'NO_PARCEL_POLYGON');
  const unavailable = notCovered || !hasSummary;

  const subtitle = data && hasSummary
    ? `Overture building outlines · ${pointBox ? '60 m box around the point' : 'parcel polygon'} · on-parcel = at least half the footprint inside the line`
    : null;

  let body;
  if (notCovered) {
    body = (
      <Note tone="muted">
        <strong>Not covered.</strong> Building footprints are not loaded for {county} yet. The source covers this kind of lookup; this county's outlines have not been loaded into the warehouse.
        {warnings.length ? <ul className="le-warning-list">{warnings.map((w) => <li key={w}>{w}</li>)}</ul> : null}
      </Note>
    );
  } else if (!hasSummary) {
    const copy = st === 'error'
      ? 'The footprint lookup failed or timed out. Every other dataset still returned — try again in a moment.'
      : st === 'not_available'
        ? 'Building footprints are not configured on the backend you are connected to.'
        : st === 'not_requested'
          ? 'Building footprints were not requested.'
          : 'No footprint summary was returned for this location.';
    body = (
      <Note tone={st === 'error' ? 'critical' : 'muted'}>
        <strong>{STATUS_LABELS[st] || 'Unavailable'}.</strong> {copy}
        {warnings.length ? <ul className="le-warning-list">{warnings.map((w) => <li key={w}>{w}</li>)}</ul> : null}
        {signals.filter((s) => s.message).map((s) => <div key={s.code || s.message} className="le-footprint-signal-text">{s.message}</div>)}
      </Note>
    );
  } else {
    const rows = buildingRows(data.buildings);
    const onParcelRows = rows.filter((b) => b.on_parcel === true);
    const crossingRows = rows.filter((b) => b.on_parcel !== true);
    const crossing = isNum(summary.structures_crossing_boundary) ? summary.structures_crossing_boundary : crossingRows.length;
    const vintage = summary.vintage || {};
    const showType = rows.some((b) => b.class || b.subtype);
    const surround = summary.surround || null;
    const none = summary.buildings_on_parcel === 0;
    body = (
      <>
        <div className="le-stat-row le-stat-row-hero">
          <StatTile
            label={pointBox ? 'Buildings in the 60 m box' : 'Buildings on parcel'}
            value={fmtNum(summary.buildings_on_parcel)}
            sub={crossing > 0 ? `+ ${fmtNum(crossing)} neighbouring structure${crossing === 1 ? '' : 's'} crossing the line` : 'none crossing the line'}
            size="lg"
            emphasis
            hint={`Footprints with at least half their area inside the ${pointBox ? 'box' : 'parcel'} (${scope.on_parcel_rule || 'share_inside >= 0.5'}); structures that merely cross the line are counted separately`}
          />
          <StatTile label="Footprint area" value={fmtSqft(summary.footprint_sqft_on_parcel)} sub={isNum(summary.largest_sqft) && summary.buildings_on_parcel > 1 ? `largest ${fmtSqft(summary.largest_sqft)}` : null} hint="Ground area of the on-parcel footprints, clipped to the parcel" />
          <StatTile label="Lot coverage" value={pointBox ? DASH : fmtPct(summary.lot_coverage_pct, 1)} sub={pointBox ? 'needs a parcel polygon' : 'footprint ÷ parcel area'} hint="Share of the parcel area under building footprints" />
          <StatTile label="Tallest" value={metres(summary.max_height_m)} sub={[feet(summary.max_height_m), isNum(summary.heights_known_pct) ? `heights known for ${fmtPct(summary.heights_known_pct, 0)}` : null].filter(Boolean).join(' · ') || null} hint="Tallest on-parcel structure with a source height; heights are known for only part of the source" />
          <StatTile label="Crossing the line" value={fmtNum(crossing)} sub="neighbours' structures" hint="Footprints that straddle the parcel boundary with less than half their area inside — presented as neighbours' structures, not the lot's" />
        </div>

        {none ? (
          <Note tone="info">
            <strong>No structures on the parcel.</strong> Overture has no building footprint at least half inside the {pointBox ? '60 m box' : 'parcel line'}{vintage.newest_update ? ` as of ${fmtDate(vintage.newest_update)}` : ''}.
            {crossing > 0 ? ` ${fmtNum(crossing)} neighbouring structure${crossing === 1 ? '' : 's'} cross${crossing === 1 ? 'es' : ''} the line; ${crossing === 1 ? 'it is' : 'they are'} not the lot's.` : ''}
          </Note>
        ) : null}

        {pointBox ? (
          <Note tone="warning">
            <strong>No parcel polygon.</strong> Counts are for a 60 m box around the point, not the lot; lot coverage and crossing structures are not computed against a real boundary.
          </Note>
        ) : null}

        {rows.length ? (
          <div className="le-table-wrap le-footprint-table">
            <table className="le-table le-table-compact">
              <thead>
                <tr>
                  <th>Structure</th>
                  <th className="num">Area</th>
                  <th className="num">Inside the line</th>
                  <th className="num">Height</th>
                  {showType ? <th>Class / subtype</th> : null}
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b, i) => {
                  const crossingRow = b.on_parcel !== true;
                  const label = crossingRow ? `Neighbouring structure` : `Building ${onParcelRows.indexOf(b) + 1}`;
                  return (
                    <tr key={b.gers_id || i} className={crossingRow ? 'le-footprint-row-crossing' : ''} title={b.gers_id ? `Overture GERS id ${b.gers_id}` : undefined}>
                      <td>
                        <span className="le-footprint-row-name">{label}</span>
                        {crossingRow ? <Pill tone="warning" className="le-footprint-row-pill">crosses boundary</Pill> : b.crosses_boundary ? <Pill tone="neutral" className="le-footprint-row-pill">touches the line</Pill> : null}
                      </td>
                      <td className="num">{fmtSqft(b.area_sqft)}</td>
                      <td className="num">{isNum(b.share_inside) ? fmtRatioPct(b.share_inside, b.share_inside < 0.1 && b.share_inside > 0 ? 1 : 0) : DASH}</td>
                      <td className="num">{isNum(b.height_m) ? `${fmtNum(b.height_m, 1)} m${isNum(b.num_floors) ? ` · ${fmtNum(b.num_floors)} fl` : ''}` : DASH}</td>
                      {showType ? <td>{[b.class, b.subtype].filter(Boolean).join(' / ') || DASH}</td> : null}
                      <td>{b.updated ? fmtDate(b.updated) : DASH}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {surround ? (
          <div className="le-footprint-surround" title="Buildings in the ring around the parcel, from the same source">
            <strong>Surroundings.</strong> {footprintSurroundSentence(surround, scope.radius_ft)}
          </div>
        ) : null}

        {signals.length ? (
          <div className="le-canopy-signals">
            {signals.map((s) => (
              <div key={s.code || s.message} className="le-canopy-signal">
                <Pill tone={signalTone(s.tone)}>{footprintSignalLabel(s.code)}</Pill>
                {s.message ? <span className="le-canopy-signal-text">{s.message}</span> : null}
              </div>
            ))}
          </div>
        ) : null}

        <Note tone="info">
          <strong>Footprints, not building types.</strong> An outline says where a structure stands and how large it is, not what it is; heights are source estimates and are missing for many buildings. Outlines are as of the source release and can lag new construction or demolition.
          {notes.length ? <ul className="le-warning-list">{notes.map((n) => <li key={n}>{n}</li>)}</ul> : null}
        </Note>

        <div className="le-footnotes">
          {vintage.oldest_update || vintage.newest_update ? (
            <span>Footprints last updated {vintage.oldest_update && vintage.newest_update && vintage.oldest_update !== vintage.newest_update ? `${fmtDate(vintage.oldest_update)} – ${fmtDate(vintage.newest_update)}` : fmtDate(vintage.newest_update || vintage.oldest_update)}</span>
          ) : null}
          <span>{FOOTPRINT_ATTRIBUTION}{source.source_id ? ` (${source.source_id})` : ''}{source.release ? ` · release ${source.release}` : ''}{source.license ? ` · ${source.license}` : ''}{scope.county_fips ? ` · county ${scope.county_fips}` : ''}{data.retrieved_at ? ` · retrieved ${fmtDateTime(data.retrieved_at)}` : ''}</span>
        </div>
      </>
    );
  }

  return (
    <SectionCard id="le-section-footprints" icon={<MdOutlineHomeWork size={20} />} title="Building footprints" subtitle={subtitle} status={status} dataset={{}} className={unavailable ? 'le-card-missing' : ''}>
      {body}
    </SectionCard>
  );
}
